/**
 * The lighting rig, extracted from `core/engine.ts` and given a brain.
 *
 * Three ideas drive this file.
 *
 * 1. **A tight shadow frustum.** A directional light's shadow map is a fixed
 *    budget of texels spread over whatever volume its ortho frustum covers. The
 *    old rig hard-coded a 44x44 world-unit box regardless of what the camera was
 *    actually looking at, so a 2048 map spent most of its texels on empty space
 *    and the visible area got maybe a quarter of them. That is *the* reason cel
 *    shadows read as mushy grey blobs instead of painted shapes. Here we fit the
 *    frustum, every frame, to the exact slab of world the orthographic camera can
 *    see between the ground plane and the scene ceiling — usually shrinking the
 *    covered area by 4-8x, which is the same as quadrupling shadow resolution for
 *    free.
 *
 * 2. **Texel snapping.** Once the frustum hugs the view it also *moves* with the
 *    view, and an unsnapped moving frustum makes shadow edges crawl and shimmer
 *    while the player walks. Quantising the frustum centre to whole shadow-map
 *    texels pins the sampling grid to the world, killing the crawl.
 *
 * 3. **Moods, not magic numbers.** Hub, home and factory want measurably
 *    different light: the hub is a bright warm showroom, the home is a golden
 *    afternoon interior, the factory is cooler and more contrasty. Presets keep
 *    those as named, tweakable data instead of numbers smeared through scenes.
 *
 * Zero allocation after construction: every vector and matrix used per frame is
 * a preallocated field.
 */
import {
  DirectionalLight,
  HemisphereLight,
  Matrix4,
  Object3D,
  OrthographicCamera,
  Scene,
  Vector3,
} from 'three';
import { srgb } from './palette';

/** Named moods. Scenes ask for one; they never poke individual lights. */
export type MoodId = 'hub' | 'home' | 'factory';

/** A directional light described the way a lighting artist thinks about it. */
export interface DirSpec {
  /** sRGB hex. */
  color: number;
  intensity: number;
  /**
   * Horizontal angle in degrees, measured RELATIVE TO THE CAMERA YAW. Keeping
   * key light relative to the camera is what stops shadows from flipping
   * direction when the camera swings between rooms — the illustration keeps a
   * consistent "sun is over there" read.
   */
  azimuthDeg: number;
  /** Angle above the horizon, degrees. Clamped to [-80, 85]. */
  elevationDeg: number;
  /** Distance from the focus point. Only matters for the key (shadow near/far). */
  distance?: number;
}

export interface ShadowSpec {
  /** Square shadow map resolution. 2048 is the sweet spot for this art style. */
  mapSize: number;
  bias: number;
  normalBias: number;
  /** PCF radius. Only honoured by PCFShadowMap / VSMShadowMap. */
  radius: number;
  /** World height above y=0 that the frustum must contain. Rooms are low. */
  ceiling: number;
  /** World units below y=0 to include (sunken floors, bin mouths). */
  floor: number;
  /** Extra world-space slack around the fitted region. */
  padding: number;
  /** How far *toward the light* casters may sit outside the fitted region. */
  casterMargin: number;
}

export interface LightingPreset {
  key: DirSpec;
  rim: DirSpec;
  /**
   * A dim upward-facing light standing in for radiosity off the cream floor.
   * Cel shading has no GI, so without this the undersides of props go dead flat.
   */
  bounce: DirSpec;
  hemi: { sky: number; ground: number; intensity: number };
  shadow: ShadowSpec;
  /** Optional linear fog, tinted to the shell green. `null` disables. */
  fog: { color: number; near: number; far: number } | null;
}

const BASE_SHADOW: ShadowSpec = {
  mapSize: 2048,
  bias: -0.00035,
  normalBias: 0.022,
  radius: 2,
  ceiling: 8,
  floor: -0.6,
  padding: 1.6,
  casterMargin: 26,
};

/**
 * Moods. Intensities look high because the renderer uses `NoToneMapping` — flat
 * cel colour needs the lighting to land near 1.0 without a filmic curve pulling
 * it back down.
 */
export const LIGHTING_PRESETS: Record<MoodId, LightingPreset> = {
  /** Bright, warm, welcoming. A showroom for the three game cards. */
  hub: {
    key: { color: 0xfff6e4, intensity: 2.55, azimuthDeg: 38, elevationDeg: 54, distance: 62 },
    rim: { color: 0xfce9cc, intensity: 0.8, azimuthDeg: 205, elevationDeg: 26 },
    bounce: { color: 0xa4cda8, intensity: 0.34, azimuthDeg: 150, elevationDeg: -24 },
    hemi: { sky: 0xdff0e4, ground: 0x3c5142, intensity: 1.0 },
    shadow: { ...BASE_SHADOW, ceiling: 9, padding: 2.2 },
    fog: null,
  },
  /** Golden domestic afternoon. Low ceiling, so the frustum can be very tight. */
  home: {
    key: { color: 0xffeec2, intensity: 2.7, azimuthDeg: 52, elevationDeg: 47, distance: 58 },
    rim: { color: 0xa4cda8, intensity: 0.68, azimuthDeg: 218, elevationDeg: 22 },
    bounce: { color: 0xfce9cc, intensity: 0.42, azimuthDeg: 160, elevationDeg: -26 },
    hemi: { sky: 0xf6ecd8, ground: 0x3c5142, intensity: 0.95 },
    shadow: { ...BASE_SHADOW, ceiling: 5.5, padding: 1.2, normalBias: 0.016 },
    fog: null,
  },
  /** Cooler, crisper, higher contrast — machinery should feel engineered. */
  factory: {
    key: { color: 0xf4fbff, intensity: 2.85, azimuthDeg: -34, elevationDeg: 58, distance: 64 },
    rim: { color: 0xf8c226, intensity: 0.95, azimuthDeg: 168, elevationDeg: 20 },
    bounce: { color: 0x74ac4b, intensity: 0.3, azimuthDeg: 20, elevationDeg: -28 },
    hemi: { sky: 0xddf2e0, ground: 0x173721, intensity: 0.82 },
    shadow: { ...BASE_SHADOW, ceiling: 11, padding: 1.8, bias: -0.0004 },
    fog: null,
  },
};

const DEG = Math.PI / 180;
const UP = new Vector3(0, 1, 0);

/**
 * World units the shadow frustum's half-extent and depth range are rounded to.
 *
 * Quarter of a unit costs at most ~2% of the shadow map's resolution at the
 * extents this scene uses, and buys a texel size and a depth mapping that only
 * change in steps — without which the texel snap re-phases every frame the
 * camera moves.
 */
const FRUSTUM_QUANTUM = 0.25;

/**
 * World units the KEY light's anchor point is rounded to.
 *
 * `placeLight` re-anchors the light to the camera focus every frame, so the
 * shadow camera's view matrix used to change continuously. Even with the
 * frustum's texel grid pinned to the world (see `fitShadowFrustum`), a view
 * matrix that drifts means the shadow map is re-rasterised from a very slightly
 * different origin every frame, and depth samples sitting on a triangle edge
 * flip back and forth. Quantising the anchor makes the whole shadow pipeline —
 * view matrix, projection, depth range — piecewise constant, so consecutive
 * frames produce a bit-identical shadow map between steps.
 *
 * A directional light's shading depends only on `position - target`, and both
 * move together here, so this is invisible in the lit image. The light sits 26
 * to 62 units away with a 26-unit caster margin, so a half-unit of anchor slop
 * cannot clip a caster.
 */
const KEY_ANCHOR_QUANTUM = 1;

export class LightingRig {
  readonly key: DirectionalLight;
  readonly fill: HemisphereLight;
  readonly bounce: DirectionalLight;
  readonly rim: DirectionalLight;
  /** Container so the whole rig can be added/removed as one node. */
  readonly root = new Object3D();

  private preset: LightingPreset;
  private mood: MoodId = 'hub';
  private shadowsEnabled = true;

  /* --- per-frame scratch: never allocate in `update` --- */
  private readonly _dir = new Vector3();
  private readonly _right = new Vector3();
  private readonly _up = new Vector3();
  private readonly _fwd = new Vector3();
  private readonly _lightPos = new Vector3();
  /** The key light's position expressed in the world-anchored light basis. */
  private readonly _lightLocal = new Vector3();
  /** The camera focus, quantised — what the key light and its target hang off. */
  private readonly _anchor = new Vector3();
  private readonly _p = new Vector3();
  private readonly _corners: Vector3[] = [];
  private readonly _m = new Matrix4();
  private readonly _mInv = new Matrix4();

  constructor(scene: Scene, mood: MoodId = 'hub') {
    for (let i = 0; i < 8; i++) this._corners.push(new Vector3());

    this.key = new DirectionalLight(0xffffff, 1);
    this.key.castShadow = true;
    this.key.shadow.camera.near = 0.5;
    this.key.shadow.camera.far = 160;

    this.fill = new HemisphereLight(0xffffff, 0x000000, 1);
    this.bounce = new DirectionalLight(0xffffff, 0.3);
    this.rim = new DirectionalLight(0xffffff, 0.8);

    // Targets must live in the graph for their world matrices to resolve.
    this.root.add(
      this.key,
      this.key.target,
      this.fill,
      this.bounce,
      this.bounce.target,
      this.rim,
      this.rim.target
    );
    scene.add(this.root);

    this.preset = LIGHTING_PRESETS[mood];
    this.applyPreset(mood);
  }

  /** Back-compat shape for `engine.lights`. */
  get lights(): {
    key: DirectionalLight;
    fill: HemisphereLight;
    bounce: DirectionalLight;
    rim: DirectionalLight;
  } {
    return { key: this.key, fill: this.fill, bounce: this.bounce, rim: this.rim };
  }

  get currentMood(): MoodId {
    return this.mood;
  }

  get currentPreset(): LightingPreset {
    return this.preset;
  }

  applyPreset(mood: MoodId): void;
  applyPreset(preset: LightingPreset): void;
  applyPreset(arg: MoodId | LightingPreset): void {
    if (typeof arg === 'string') {
      this.mood = arg;
      this.preset = LIGHTING_PRESETS[arg];
    } else {
      this.preset = arg;
    }
    const p = this.preset;

    this.key.color.copy(srgb(p.key.color));
    this.key.intensity = p.key.intensity;

    this.rim.color.copy(srgb(p.rim.color));
    this.rim.intensity = p.rim.intensity;

    this.bounce.color.copy(srgb(p.bounce.color));
    this.bounce.intensity = p.bounce.intensity;

    this.fill.color.copy(srgb(p.hemi.sky));
    this.fill.groundColor.copy(srgb(p.hemi.ground));
    this.fill.intensity = p.hemi.intensity;

    const s = this.key.shadow;
    if (s.mapSize.x !== p.shadow.mapSize) {
      s.mapSize.set(p.shadow.mapSize, p.shadow.mapSize);
      // The GPU-side map is sized on first use; drop it so it is reallocated.
      s.map?.dispose();
      s.map = null;
      s.mapPass?.dispose();
      s.mapPass = null;
    }
    s.bias = p.shadow.bias;
    s.normalBias = p.shadow.normalBias;
    s.radius = p.shadow.radius;
    this.key.castShadow = this.shadowsEnabled;
  }

  /** Global quality lever: mobile / reduced-power can drop shadows entirely. */
  setShadowsEnabled(on: boolean): void {
    this.shadowsEnabled = on;
    this.key.castShadow = on;
  }

  /** Halve or quarter the shadow map without changing the mood. */
  setShadowMapSize(size: number): void {
    const s = this.key.shadow;
    if (s.mapSize.x === size) return;
    s.mapSize.set(size, size);
    s.map?.dispose();
    s.map = null;
    s.mapPass?.dispose();
    s.mapPass = null;
  }

  /**
   * Reposition the rig around `focus` and refit the shadow frustum to what the
   * camera can actually see. Call once per frame, after the camera has moved.
   *
   * @param camera  the main orthographic camera (already oriented)
   * @param focus   world-space point the camera is looking at
   * @param yawRad  camera yaw, so light azimuths stay camera-relative
   */
  update(camera: OrthographicCamera, focus: Vector3, yawRad: number): void {
    const p = this.preset;

    // Only the key light casts, and only the key light needs a still anchor.
    const q = KEY_ANCHOR_QUANTUM;
    this._anchor.set(
      Math.round(focus.x / q) * q,
      Math.round(focus.y / q) * q,
      Math.round(focus.z / q) * q
    );

    this.placeLight(this.key, p.key, this._anchor, yawRad, p.key.distance ?? 60);
    this.placeLight(this.rim, p.rim, focus, yawRad, p.rim.distance ?? 40);
    this.placeLight(this.bounce, p.bounce, focus, yawRad, p.bounce.distance ?? 26);
    this.fill.position.set(focus.x, focus.y + 20, focus.z);

    if (this.shadowsEnabled) this.fitShadowFrustum(camera, focus, this._anchor);
  }

  private placeLight(
    light: DirectionalLight,
    spec: DirSpec,
    focus: Vector3,
    yawRad: number,
    distance: number
  ): void {
    const az = spec.azimuthDeg * DEG + yawRad;
    const el = Math.max(-80, Math.min(85, spec.elevationDeg)) * DEG;
    const ce = Math.cos(el);
    this._dir.set(Math.sin(az) * ce, Math.sin(el), Math.cos(az) * ce);

    light.position.copy(focus).addScaledVector(this._dir, distance);
    light.target.position.copy(focus);
    light.target.updateMatrixWorld();
    light.updateMatrixWorld();
  }

  /**
   * Fit the key light's ortho shadow frustum to the world volume that is both
   * on-screen and inside the scene's vertical slab.
   *
   * Method: take the four corners of the camera's viewport rectangle at the
   * focus plane, then slide each along the camera's forward axis until it hits
   * y = floor and y = ceiling. Those eight world points bound exactly the region
   * a 2.5D scene can put geometry in and have it be visible. Transform them into
   * a world-anchored light basis, take the AABB, snap to the shadow texel grid,
   * and only then subtract the light's own position to get the frustum the
   * shadow camera actually wants.
   */
  private fitShadowFrustum(camera: OrthographicCamera, focus: Vector3, anchor: Vector3): void {
    const sp = this.preset.shadow;
    const sc = this.key.shadow.camera;

    camera.updateMatrixWorld();
    this._right.setFromMatrixColumn(camera.matrixWorld, 0).normalize();
    this._up.setFromMatrixColumn(camera.matrixWorld, 1).normalize();
    this._fwd.setFromMatrixColumn(camera.matrixWorld, 2).normalize().negate();

    const zoom = camera.zoom || 1;
    const hw = ((camera.right - camera.left) * 0.5) / zoom;
    const hh = ((camera.top - camera.bottom) * 0.5) / zoom;

    // A near-horizontal camera can't be intersected with horizontal planes;
    // fall back to a sphere around the focus point.
    const fy = this._fwd.y;
    const canProject = Math.abs(fy) > 1e-3;
    const reach = canProject ? Math.min(Math.abs(sp.ceiling - sp.floor) / Math.abs(fy), 400) : 0;

    let ci = 0;
    for (let sx = -1; sx <= 1; sx += 2) {
      for (let sy = -1; sy <= 1; sy += 2) {
        this._p
          .copy(focus)
          .addScaledVector(this._right, hw * sx)
          .addScaledVector(this._up, hh * sy);

        if (canProject) {
          // Where this screen corner meets the ceiling plane, and the floor plane.
          const tHigh = (sp.ceiling - this._p.y) / fy;
          const tLow = (sp.floor - this._p.y) / fy;
          const a = this._corners[ci++]!;
          a.copy(this._p).addScaledVector(this._fwd, Math.max(-reach, Math.min(reach, tHigh)));
          const b = this._corners[ci++]!;
          b.copy(this._p).addScaledVector(this._fwd, Math.max(-reach, Math.min(reach, tLow)));
        } else {
          const a = this._corners[ci++]!;
          a.copy(this._p).setY(sp.ceiling);
          const b = this._corners[ci++]!;
          b.copy(this._p).setY(sp.floor);
        }
      }
    }

    // World -> light basis, ORIGIN PINNED TO THE WORLD ORIGIN.
    //
    // This used to carry `setPosition(this._lightPos)`, which put the origin on
    // the light itself — and `placeLight` re-anchors the light to the camera
    // focus every single frame. Snapping a coordinate that is measured from a
    // continuously moving ruler snaps nothing, so the shadow texel grid slid
    // with the camera and every shadow edge crawled. `Matrix4.lookAt` writes
    // only the rotation, so leaving the translation at zero gives a basis whose
    // origin is fixed in the world; the light's own offset is subtracted back
    // out below, after the snap, where it cannot contaminate the rounding.
    //
    // Look at `anchor`, not `focus`: the anchor is what the light is actually
    // aimed at, so the basis rotation is exactly the preset's azimuth and
    // elevation and holds perfectly still between anchor steps.
    this._lightPos.copy(this.key.position);
    this._m.identity();
    this._m.lookAt(this._lightPos, anchor, UP);
    this._mInv.copy(this._m).invert();
    this._lightLocal.copy(this._lightPos).applyMatrix4(this._mInv);

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (let i = 0; i < 8; i++) {
      const c = this._corners[i]!.applyMatrix4(this._mInv);
      if (c.x < minX) minX = c.x;
      if (c.x > maxX) maxX = c.x;
      if (c.y < minY) minY = c.y;
      if (c.y > maxY) maxY = c.y;
      if (c.z < minZ) minZ = c.z;
      if (c.z > maxZ) maxZ = c.z;
    }
    if (!Number.isFinite(minX) || !Number.isFinite(maxZ)) return;

    // Square extent keeps texel density isotropic and stable under camera yaw,
    // and rounding it UP onto a fixed ladder keeps the texel SIZE constant while
    // the camera zooms. A snap grid is only a grid if its spacing holds still:
    // let `ext` drift continuously and `texel` drifts with it, which re-phases
    // the whole grid every frame no matter where its origin is.
    let ext = Math.max(maxX - minX, maxY - minY) * 0.5 + sp.padding;
    ext = Math.max(ext, 1);
    ext = Math.ceil(ext / FRUSTUM_QUANTUM) * FRUSTUM_QUANTUM;

    // Texel snapping, now in world-anchored coordinates: the grid is bolted to
    // the world, so panning the camera slides the frustum across a stationary
    // grid instead of dragging the grid along with it.
    const texel = (ext * 2) / sp.mapSize;
    const cx = Math.round(((minX + maxX) * 0.5) / texel) * texel - this._lightLocal.x;
    const cy = Math.round(((minY + maxY) * 0.5) / texel) * texel - this._lightLocal.y;

    // The shadow camera's own view space is centred on the light, so the light's
    // offset comes back out here — after the rounding, never before it.
    const near = maxZ - this._lightLocal.z;
    const far = minZ - this._lightLocal.z;

    sc.left = cx - ext;
    sc.right = cx + ext;
    sc.bottom = cy - ext;
    sc.top = cy + ext;
    // In light view space the light looks down -Z, so points in front have z < 0.
    // Quantised outward for the same reason `ext` is: a depth range that drifts
    // re-maps every stored depth every frame, which walks samples across the
    // bias threshold and makes shadow edges fizz.
    const q = FRUSTUM_QUANTUM;
    sc.near = Math.max(0.1, Math.floor((-near - sp.casterMargin) / q) * q);
    sc.far = Math.max(sc.near + 1, Math.ceil((-far + sp.padding) / q) * q);
    sc.updateProjectionMatrix();
  }

  /** Debug helper: the world-space area (in units²) the shadow map covers. */
  get shadowCoverage(): number {
    const sc = this.key.shadow.camera;
    return (sc.right - sc.left) * (sc.top - sc.bottom);
  }

  dispose(): void {
    this.key.shadow.map?.dispose();
    this.key.shadow.mapPass?.dispose();
    this.key.dispose();
    this.rim.dispose();
    this.bounce.dispose();
    this.fill.dispose();
    this.root.removeFromParent();
    this.root.clear();
  }
}
